import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { IUserInfo } from "../../store/actions";
import { useSelector } from "react-redux";

interface DeleteChecklistButtonProps {
  chassisNo: string;
  rework_type: string;
  onClearComplete?: () => void;
}

const DeleteChecklistButton: React.FC<DeleteChecklistButtonProps> = ({
  chassisNo,
  rework_type,
  onClearComplete,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [open, setOpen] = useState(false);
  console.log("chassisNo:", chassisNo);
  console.log("rework_type:", rework_type);

  const authInfo: IUserInfo = useSelector(
    (state: any) => state.userAuthInfoState
  );
  const userRole = authInfo.roles;

  const hasRequiredRole = Array.isArray(userRole)
    ? userRole.includes("qa-manager-qc")
    : userRole === "qa-manager-qc";

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!chassisNo || !rework_type) {
      toast.error("Chassis number and rework type are required.");
      handleClose();
      return;
    }

    const cleanedReworkType = rework_type.replace("_rework", "");

    const googleDriveApiUrl = process.env.REACT_APP_GOOGLE_DRIVE_API_BASE_URL;
    const deleteImagesEndpoint = `${googleDriveApiUrl}/delete-all-images`;
    console.log("deleteImagesEndpoint:", deleteImagesEndpoint);

    const imagesPayload = {
      chassis_no: `DC${chassisNo}`,
      station_name: cleanedReworkType,
    };
    console.log("Sending delete images request:", imagesPayload);

    try {
      await toast.promise(
        axiosPrivate.post(deleteImagesEndpoint, imagesPayload),
        {
          pending: "Deleting images...",
          success: "Images deleted successfully!",
          error: "No images found. Unable to delete.",
        }
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(
        "Error deleting images:",
        axiosError.response?.data,
        axiosError.message
      );
    }

    const qcApiUrl = process.env.REACT_APP_API_BASE_URL;
    const deleteQcChecklistEndpoint = `${qcApiUrl}/api/clear_checklist_inspection/${chassisNo}`;
    console.log("deleteQcChecklistEndpoint:", deleteQcChecklistEndpoint);

    try {
      await toast.promise(
        axiosPrivate.post(deleteQcChecklistEndpoint, {
          chassis_no: chassisNo,
          station_name: cleanedReworkType,
        }),
        {
          pending: "Deleting data...",
          success: "Data deleted successfully!",
          error: "Item not found. Unable to delete.",
        }
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(
        "Error deleting checklist:",
        axiosError.response?.data,
        axiosError.message
      );
    }

    if (onClearComplete) {
      onClearComplete();
    }

    handleClose();
  };

  if (!hasRequiredRole) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="error"
        onClick={handleClickOpen}
        disabled={!chassisNo || !rework_type}
      >
        Clear Data
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete all images and checklist data for
            chassis DC{chassisNo}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteChecklistButton;
